import {
  CalendarCheckIcon, MapPinIcon, UsersIcon, SparklesIcon,
  ChartNoAxesCombinedIcon, Settings2Icon, CalendarDaysIcon, ShieldCheckIcon
} from 'lucide-react';

export const publicNavigation = [
  { path: '/welcome', label: 'Welcome' },
  { path: '/features', label: 'Features' },
  { path: '/faq', label: 'FAQ' }
] as const;

export type PublicPage = 'welcome' | 'features' | 'faq';

/** Explicit public URLs only. Tenant and application URLs retain their existing guards. */
export function publicPageForPath(pathname: string): PublicPage | null {
  const path = pathname.replace(/\/+$/, '') || '/';
  if (path === '/welcome') return 'welcome';
  if (path === '/features') return 'features';
  if (path === '/faq') return 'faq';
  return null;
}

export const publicFeatures = [
  {
    id: 'booking', icon: CalendarCheckIcon, title: 'A place for every kind of day',
    summary: 'Find and book the shared resources your organisation makes available.',
    details: 'Choose a desk for focused work, a room to meet, or a parking space for your visit. Search by location, time and resource type, and review the details before confirming.',
    items: ['Location and time filters', 'Your bookings in one place', 'Check-in and cancellation, subject to policy']
  },
  {
    id: 'maps', icon: MapPinIcon, title: 'See where you will be',
    summary: 'Find your bearings before you arrive.',
    details: 'Where your organisation has uploaded floor plans and placed resources, explore the map alongside availability to find a suitable space.',
    items: ['Floor-plan browsing', 'Resource details on the map', 'Map and list booking options']
  },
  {
    id: 'planning', icon: CalendarDaysIcon, title: 'Make time together count',
    summary: 'Plan office days around the people you work with.',
    details: 'Use My Work Plan to share your intended working location, coordinate with your teams and find colleagues according to their sharing choices.',
    items: ['Personal work plans', 'Team planning and guidance', 'Presence-aware colleague search']
  },
  {
    id: 'visitors', icon: UsersIcon, title: 'Bring people and services together',
    summary: 'Support the wider working day, beyond a desk.',
    details: 'When enabled, visitor tools help hosts manage visits, while staffed services let people request offerings from the teams responsible for delivering them.',
    items: ['Visitor hosting and records', 'Staffed-service requests', 'Responsible-team request queues']
  },
  {
    id: 'assistant', icon: SparklesIcon, title: 'Ask for a little assistance',
    summary: 'Explore options in everyday language.',
    details: 'When your organisation enables the AI assistant, ask for help finding spaces and planning your day. Proposed changes require your confirmation and follow your existing permissions.',
    items: ['Conversational search and guidance', 'Review before confirming changes', 'Organisation-controlled access']
  },
  {
    id: 'insights', icon: ChartNoAxesCombinedIcon, title: 'Understand how places are used',
    summary: 'Give workplace teams a clearer view of their resources.',
    details: 'Authorised administrators can use dashboards and reports to review booking activity, utilisation and no-shows, then make informed choices about shared spaces.',
    items: ['Workplace dashboards', 'Utilisation and booking reports', 'Access controlled by role and permission']
  },
  {
    id: 'administration', icon: Settings2Icon, title: 'Shape the workplace experience',
    summary: 'Manage the places, rules and features your organisation needs.',
    details: 'Administrators maintain locations, spaces, amenities and booking policies. Organisation settings control available modules and permitted branding changes.',
    items: ['Inventory and policy management', 'Organisation and entity settings', 'Role-based administration']
  },
  {
    id: 'access', icon: ShieldCheckIcon, title: 'The right access, in the right place',
    summary: 'Keep workplace information within its intended audience.',
    details: 'Sign in with your Microsoft work account to access the organisations and entities you are authorised to use. Permissions and sharing choices determine which tools and information you see.',
    items: ['Microsoft work-account sign-in', 'Organisation-scoped access', 'Personal presence-sharing choices']
  }
] as const;

export const publicFaqs = [
  {
    id: 'what', question: 'What is Commons Foundry?',
    answer: 'Commons Foundry helps people make better use of shared places and resources. Use it to find and book spaces, plan working days with colleagues and manage your reservations. Workplace teams can manage inventory, policies and reporting.'
  },
  {
    id: 'signin', question: 'How do I sign in?',
    answer: 'Select Sign in and use the Microsoft work account your organisation has authorised for this application. Once signed in, select Open application if you are still on these information pages. You may be asked to select an organisation or entity before you begin.'
  },
  {
    id: 'access', question: 'Do I need an invitation or an account?',
    answer: 'You need an authorised work account and access to the relevant organisation or entity. Signing in does not automatically grant membership or administrator permissions. If you cannot see your organisation, contact your workplace or IT administrator to arrange access.'
  },
  {
    id: 'resources', question: 'What can I book?',
    answer: 'Your organisation decides which resource types and locations to offer. These may include desks, meeting rooms, parking and other shared resources. Sign in to see your actual inventory, availability and booking rules.'
  },
  {
    id: 'booking', question: 'How do I make or change a booking?',
    answer: 'Open Book, choose where and when you need a resource, review the available options and confirm your choice. Use My Bookings to review, change or cancel reservations where the booking policy permits. Check-in requirements and cancellation limits vary by organisation.'
  },
  {
    id: 'checkin', question: 'Do I need to check in when I arrive?',
    answer: 'Some resources require check-in. Review your booking and follow the instructions your organisation provides. Its policies determine check-in windows and what happens to a booking when you do not check in.'
  },
  {
    id: 'mobile', question: 'Can I use it on my phone?',
    answer: 'Yes. You can use Commons Foundry in your phone’s web browser. After signing in, the mobile Today experience provides a view of your day and access to booking. Available actions depend on the features your organisation has enabled.'
  },
  {
    id: 'sharing', question: 'Can everyone see my work plan?',
    answer: 'Work-plan and colleague views follow your sharing choices and the organisation’s access rules. Review your presence-sharing settings before sharing your plans. These public information pages do not display people, bookings or workplace availability.'
  },
  {
    id: 'features', question: 'Why can I not see every feature listed here?',
    answer: 'Your organisation chooses which modules to enable, which resources to make available and how its connections are configured. Your membership, role and permissions also affect what you can use. The features page describes the product’s capabilities, not a promise that every tool is enabled for every account.'
  },
  {
    id: 'administration', question: 'Who can manage spaces, policies and users?',
    answer: 'Administration is permission-based. Space admins typically manage workplace inventory and policies. IT admins also manage workplace role assignments and integrations. Entity, tenant and platform administration have separate permissions. Contact your administrator if you need additional access.'
  },
  {
    id: 'help', question: 'Who should I contact if I need help?',
    answer: 'Contact your organisation’s workplace, facilities or IT support team for access, booking rules, missing resources or sign-in problems. If sign-in fails, check that you selected the correct work account and share the error message with your support team. Never share your password.'
  }
] as const;
